import { intersection } from 'ramda';

import { getTermsValue, hasTerm } from './helpers';
import {
  type FDealType,
  type FOfferType,
  type NonEmptyArray,
  type TJsonQuery,
  type TJsonQueryOptionalKeys,
} from './types';
import { getDealTypeFromJsonQuery, getOfferTypeFromJsonQuery } from './utils';

export type TAvailabilityFunction = (jsonQuery: TJsonQuery) => boolean;

export function isAvailable(dealType: FDealType, offerType: FOfferType): TAvailabilityFunction {
  return jsonQuery => {
    const nextDealType = getDealTypeFromJsonQuery(jsonQuery);
    const nextOfferType = getOfferTypeFromJsonQuery(jsonQuery);

    return (dealType & nextDealType) !== 0 && (offerType & nextOfferType) !== 0;
  };
}

export function isAvailableStrict(dealType: FDealType, offerType: FOfferType): TAvailabilityFunction {
  return jsonQuery => {
    const nextDealType = getDealTypeFromJsonQuery(jsonQuery);
    const nextOfferType = getOfferTypeFromJsonQuery(jsonQuery);

    return nextDealType === dealType && nextOfferType === offerType;
  };
}

export function isAvailableRegion(regions: NonEmptyArray<number>): TAvailabilityFunction {
  return jsonQuery => {
    const region = getTermsValue('region')(jsonQuery);

    return !!region && intersection(regions, region).length > 0;
  };
}

export function isUnavailableRegion(regions: NonEmptyArray<number>): TAvailabilityFunction {
  return jsonQuery => {
    const region = getTermsValue('region')(jsonQuery);

    return !!region && intersection(regions, region).length === 0;
  };
}

export function isAvailableIfKeysUnset(keys: NonEmptyArray<TJsonQueryOptionalKeys>): TAvailabilityFunction {
  return jsonQuery => {
    return !keys.some(k => hasTerm(k)(jsonQuery));
  };
}

export function oneOf(
  ...functions: [TAvailabilityFunction, TAvailabilityFunction, ...TAvailabilityFunction[]]
): TAvailabilityFunction {
  return jsonQuery => {
    return functions.some(f => f(jsonQuery));
  };
}

export function everyOf(
  ...functions: [TAvailabilityFunction, TAvailabilityFunction, ...TAvailabilityFunction[]]
): TAvailabilityFunction {
  return jsonQuery => {
    return functions.every(f => f(jsonQuery));
  };
}
